/* eslint-disable react/forbid-prop-types */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import DashboardChart from 'components/Chart';
import { useGetProjectStatisticsYear } from 'utils/hooks/dashboard';
import Loading from 'components/Loading';
import { statusColors } from 'utils/func/dashboard';
import DashboardFilter from '../DashboardFilter';

export default function ProjectStackBarChart({ programs, organizations }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    yearProposed: null,
    organizationId: [],
    programId: [],
  });

  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: null,
    organizationId: [],
    programId: [],
  });

  const { data, isLoading } = useGetProjectStatisticsYear(appliedFilters, {
    refetchOnWindowFocus: false,
    enabled: isLoadedFilterFromLocalStorage,
  });

  const hasActiveFilters =
    appliedFilters.yearProposed ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

  const statuses = [
    'ongoingProject',

    'midtermQualifiedProject',

    'midtermNotQualifiedProject',

    'acceptanceQualifiedProject',

    'acceptanceNotQualifiedProject',

    'overdueProject',
  ];

  const chartData = useMemo(() => {
    const labels = data?.map((item) => item?.label) || [];

    const mapStyles = Object.values(statusColors.submit);

    const datasets = [
      ...mapStyles.map((color, index) => ({
        type: 'bar',
        label: t(`dashboard.projectSubmit.label.${statuses?.[index]}`),
        backgroundColor: color,
        data: data?.map((item) => item.datasets[index]?.count),
      })),
    ];

    return {
      labels,
      datasets,
    };
  }, [data, t]);

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      aspectRatio: 0.5,
      plugins: {
        tooltips: {
          mode: 'index',
          intersect: false,
        },
        legend: {
          labels: {
            color: textColor,
            generateLabels(chart) {
              const { data: _data } = chart;
              return _data.datasets.map((dts, i) => {
                const value = dts.data.reduce((sum, val) => sum + val, 0);
                return {
                  text: `${dts.label} (${value})`,
                  fillStyle: dts.backgroundColor,
                  strokeStyle: dts.borderColor || dts.backgroundColor,
                  lineWidth: 1,
                  datasetIndex: i,
                  hidden: !chart.isDatasetVisible(i),
                };
              });
            },
          },
          position: 'bottom',
        },
        datalabels: {
          formatter: (value) => {
            if (value === 0) {
              return null;
            }

            return value.toLocaleString();
          },
          font: {
            weight: 'bold',
            size: 12,
          },
          anchor: 'end',
          align: 'top',
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
      onHover: (event, activeElements) => {
        const canvas = event.native.target;
        canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const { datasetIndex, index: clickedIndex } = elements[0];
          const clickedItem = chartData.labels[clickedIndex];

          const projectSubmitFilters = {
            yearProposed: !appliedFilters.yearProposed
              ? [parseInt(clickedItem, 10)]
              : [appliedFilters.yearProposed],
            organizationId: appliedFilters.organizationId,
            programId: appliedFilters.programId,
            projectStatus: [statuses[datasetIndex]],
          };

          localStorage.setItem(
            'admin-projects-submit-filters',
            JSON.stringify(projectSubmitFilters)
          );
          navigate('/projects-submit');
        }
      },
      interaction: {
        intersect: false,
      },
    }),
    [appliedFilters, chartData]
  );

  const formatShowAppliedFilters = () => {
    let text = '';
    if (appliedFilters.yearProposed) {
      text += `${t('dashboard.projectRevise.dropdown.yearProposed')}: ${
        appliedFilters.yearProposed
      }; `;
    }
    if (Array.isArray(appliedFilters.organizationId) && appliedFilters.organizationId.length > 0) {
      text += `${t(
        'dashboard.projectRevise.dropdown.organization'
      )}: ${appliedFilters.organizationId
        .map((id) => organizations.find((org) => org.id === id)?.name)
        .join(', ')}; `;
    }
    if (Array.isArray(appliedFilters.programId) && appliedFilters.programId.length > 0) {
      text += `${t('dashboard.projectRevise.dropdown.program')}: ${appliedFilters.programId
        .map((id) => programs.find((prog) => prog.id === id)?.name)
        .join(', ')};`;
    }
    text = text.trim().replace(/;$/, '');
    return <div className="text-wrap">{text}</div>;
  };

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-submit-year-filters');
    if (!savedFilters) return;

    setFilters(JSON.parse(savedFilters));
    setAppliedFilters(JSON.parse(savedFilters));
  }, []);

  if (isLoading) {
    return <Loading />;
  }
  return (
    <div className="card p-3 md:p-5">
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.projectSubmit.progressTitle')}
          </h3>

          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        isSelectOne={{
          yearProposed: true,
          organizationId: false,
          programId: false,
        }}
        localStorageKey="admin-dashboard-projects-submit-year-filters"
      />
      <div className="flex flex-column align-items-center px-4 pb-4 gap-2">
        {formatShowAppliedFilters()}
        <DashboardChart data={chartData} options={options} type="bar" className=" w-full" />
      </div>
    </div>
  );
}

ProjectStackBarChart.propTypes = {
  programs: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};